import { Form, Card, Button, Input, Select, Spin, Modal, Alert, Checkbox, AutoComplete, Row, Col } from 'antd';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppConfig, container } from 'src/inversify.config';
import { nameof } from '../../core/utils/object';
import {
  CoreDiameter,
  DeviceType,
  LicenseCreationAttributes,
  LicenseCreationAttributesStore,
  LicenseDataItem,
  LicenseDataItemStore,
  LicenseDataSummaryItem,
  LicenseDataSummaryStore,
  deviceTypeLabels,
  deviceTypeSorter,
  getLicenseToolCode,
  getSmartCoreLicenseToolDiameter,
  getUnlimitedLicenseAttributes,
  hoursToDateDiff,
  isHourBasedLicense,
} from '../../stores/licensing-store';
import { Query } from '../../core/stores/data-store';
import { setHeader, setRoutes } from '../../core/ui/shell';
import i18next from '../../i18n';
import moment from 'moment';
import { useIdentity } from 'src/core/services/authentication';
import { getCurrentOrg, getDateFormat } from '../../common/utils';
import { ENV } from 'src/';
import ShowLicenseDialog from '../licenses/license-show';
import { useBlocker, useParams } from 'react-router-dom';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { IdentityType, PermissionsConstants } from 'src/common/types';
import DatePicker from 'src/core/ui/date-components/date-picker';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';

const { RangePicker } = DatePicker;
const permissions = [PermissionsConstants.ApproveLicenses, PermissionsConstants.ManageGlobalLicenses];

const LicenseInfoPage: FC = () => {
  const identity = useIdentity();
  const unlimitedLicenseDefaults = getUnlimitedLicenseAttributes();
  const { t } = useTranslation();
  const params = useParams();
  const config: AppConfiguration = useContext(AppConfig);
  const [form] = Form.useForm();
  let licenseDataId = params.id!;
  const currentOrg = getCurrentOrg(identity);
  const currentUser = identity.profile['sub'];
  const inputRef = useRef();
  const licenseItemStore = container.get(LicenseDataItemStore);
  const licenseItemState = licenseItemStore.state;
  const licenseStateValue = licenseItemState.item;
  const licenseDataStore = container.get(LicenseDataSummaryStore);
  const licenseDataState = licenseDataStore.state;
  const isBusy = licenseItemState.isBusy;
  const [dateRange, setDateRange] = useState<[moment.Moment, moment.Moment]>();
  const [valuesUnchanged, setValuesUnchanged] = useState(true);
  const [customError, setCustomError] = useState('');
  const [saveLicenseDataWarning, setSaveLicenseDataWarning] = useState(false);
  const [projectOptions, setProjectOptions] = useState<string[]>([]);
  const [customerOptions, setCustomerOptions] = useState<string[]>([]);
  const [canCreateUnlimitedLicense, setCanCreateUnlimitedLicense] = useState<boolean>();
  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const licenseCreationAttributesState = licenseCreationAttributesStore.state;
  const [licenseVisible, setLicenseVisible] = useState(false);
  const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType>();
  const organizationIdRef = useRef<string | null>();
  const existingLicenseRef = useRef<LicenseDataSummaryItem>();

  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const canApproveLicenses =
    !checkingPermissions &&
    (userPermissionsSet.has(PermissionsConstants.ApproveLicenses) || userPermissionsSet.has(PermissionsConstants.ManageGlobalLicenses));

  let blocker = useBlocker(({ currentLocation, nextLocation }) => !valuesUnchanged && currentLocation.pathname !== nextLocation.pathname);

  const { TextArea } = Input;
  useEffect(() => {
    licenseCreationAttributesStore.getByUserScope({ identityId: currentUser, scopeId: currentOrg.id, identityType: IdentityType.User }).then(() => {
      licenseItemStore.load(licenseDataId).then(() => {
        setCanCreateUnlimitedLicense(licenseItemState.item.value.unlimited);
        setSelectedDeviceType(licenseItemState.item.value.deviceType);
      });
    });
  }, [inputRef]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (licenseItemState.item.value) onCancelItem();
  }, [licenseItemState]); // eslint-disable-line react-hooks/exhaustive-deps

  if (licenseItemState.item.value) {
    setHeader(t('LicenseDetails'));

    setRoutes([
      { path: '', breadcrumbName: t('Home') },
      { path: 'licenses', breadcrumbName: t('Licenses') },
      {
        path: `licenses/${getLicenseToolCode(licenseItemState.item.value as LicenseDataItem)}`,
        breadcrumbName: `${getLicenseToolCode(licenseItemState.item.value as LicenseDataItem)}`,
      },
    ]);
  }
  const getStartDate = (date: moment.Moment) => {
    date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    date.add(date.utcOffset(), 'minutes');
    return moment.utc(date);
  };
  const getEndDate = (date: moment.Moment) => {
    date.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
    date.add(date.utcOffset(), 'minutes');
    return moment.utc(date);
  };
  const validateLicense = async () => {
    let item: any;
    try {
      item = (await form.validateFields()) as LicenseDataItem;
      if (item.deviceType === DeviceType.SCTD) {
        item.toolCode = item.toolCode.padStart(4, '0');
        item.toolCode = `${item.coreDiameter}${item.toolCode}`;
      }
    } catch {
      setCustomError(t('PleaseCheckTheInfoProvided'));
      return;
    }

    item.fromDate = getStartDate(dateRange ? dateRange![0] : moment((licenseItemState.item.value as LicenseDataItem).fromDate));
    item.untilDate = getEndDate(dateRange ? dateRange[1] : moment((licenseItemState.item.value as LicenseDataItem).untilDate));
    item.id = licenseDataId;

    let fromDate: Date;
    let untilDate: Date;

    if (!isHourBasedLicense(item.deviceType)) {
      fromDate = item.fromDate?.toDate();
      untilDate = item.untilDate?.toDate();
    } else {
      fromDate = hoursToDateDiff(item.workingHours).from;
      untilDate = hoursToDateDiff(item.workingHours).until;
    }

    const fromDateDayStart = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
    const untilDateDayStart = new Date(untilDate.getFullYear(), untilDate.getMonth(), untilDate.getDate());
    const from = moment(fromDateDayStart).toISOString();
    const until = moment(untilDateDayStart).toISOString();

    const filter = `(FromDate ge ${from} and FromDate le ${until}) or (UntilDate ge ${from} and UntilDate le ${until}) or (FromDate le ${from} and UntilDate ge ${until})`;
    await licenseDataStore.load({ filter: [`id eq ${licenseItemState.item.id.value}`] });
    const licenseOrganizationId = licenseDataState.items.find(() => true)?.organizationId.value;
    organizationIdRef.current = licenseOrganizationId ?? null;

    if (validateLicenseAttributes(item)) {
      let toolCodeFilter: string | { and: string[] };
      if (item.deviceType !== DeviceType.SCTD || item.toolCode[0] !== '0') {
        toolCodeFilter = item.toolCode;
      } else {
        toolCodeFilter = {
          and: [`substring(concat('00000', ToolCode), length(concat('00000', ToolCode)) sub 5) eq '${item.toolCode}'`],
        };
      }

      var query = {
        filter: {
          OrganizationId: { and: `OrganizationId eq ${licenseOrganizationId ?? null}` },
          ToolCode: toolCodeFilter,
          DeviceType: item.deviceType,
          Unlimited: item.unlimited,
          Project: item.project,
          Customer: item.customer,
        },
        skip: 0,
        take: 2,
      } as Query;
      if (!item.unlimited) {
        query.filter = {
          ...query.filter,
          DateFilter: item.unlimited ? undefined : { and: [filter] },
        };
      }

      setCustomError('');

      var result = await licenseDataStore.licenseExists(query, licenseItemState.item.value.id);
      existingLicenseRef.current = result;
      if (result) {
        setSaveLicenseDataWarning(true);
        return;
      } else {
        onEditItem();
      }
    }
  };
  const validateLicenseAttributes = (item: any) => {
    if (licenseCreationAttributesState.item.value) {
      if (!licenseCreationAttributesState.item.value.allowedDevices.find((x) => x === item.deviceType)) {
        setCustomError(t('DeviceTypeNotAllowed'));
        return false;
      } else if (
        !item.unlimited &&
        moment((licenseCreationAttributesState.item.value as LicenseCreationAttributes).maximumLicenseDueDate).isBefore(item.untilDate, 'day')
      ) {
        setCustomError(
          t('MaximumLicenseDueDateReached').replace(
            '{0}',
            moment((licenseCreationAttributesState.item.value as LicenseCreationAttributes).maximumLicenseDueDate).format(getDateFormat())
          )
        );
        return false;
      } else if (
        !item.unlimited &&
        !isHourBasedLicense(item.deviceType) &&
        item.untilDate.diff(item.fromDate, 'days') > (licenseCreationAttributesState.item.value as LicenseCreationAttributes).maxLicensesPeriodInDays
      ) {
        setCustomError(
          t('MaxLicensesPeriodInDaysReached').replace(
            '{0}',
            (licenseCreationAttributesState.item.value as LicenseCreationAttributes).maxLicensesPeriodInDays.toString()
          )
        );
        return false;
      } else if ((licenseCreationAttributesState.item.value as LicenseCreationAttributes).maxNumberOfLicensesInCurrentMonth <= 0) {
        setCustomError(t('MaxNumberOfLicensesPerMonthReached'));
        return false;
      }
    }
    return true;
  };
  const onEditItem = async () => {
    let item: any;
    try {
      item = (await form.validateFields()) as LicenseDataItem;
      if (item.deviceType === DeviceType.SCTD) {
        item.toolCode = `${item.coreDiameter}${item.toolCode}`;
      }
    } catch {
      setCustomError(t('PleaseCheckTheInfoProvided'));
      return;
    }

    if (item.unlimited) {
      if (isHourBasedLicense(item.deviceType)) {
        item.workingHours = unlimitedLicenseDefaults.workingHours;
      } else {
        item.fromDate = unlimitedLicenseDefaults.fromDate;
        item.untilDate = unlimitedLicenseDefaults.untilDate;
      }
    } else {
      if (!isHourBasedLicense(item.deviceType)) {
        item.fromDate = getStartDate(dateRange ? dateRange![0] : moment((licenseItemState.item.value as LicenseDataItem).fromDate));
        item.untilDate = getEndDate(dateRange ? dateRange[1] : moment((licenseItemState.item.value as LicenseDataItem).untilDate));
      }
    }
    item.id = licenseDataId;
    setCustomError('');

    let response = await licenseItemStore.update(item);
    if (response) {
      if (response.isSuccess) {
        licenseItemStore.load(licenseDataId);
        setValuesUnchanged(true);
      } else {
        const msg = config.environment === ENV.DEV ? response.error : t('ErrorEditingLicenseAttributes');
        setCustomError(msg as string);
      }
      setSaveLicenseDataWarning(false);
    }
  };
  const onDateRangeEdit = async (dateRange: any) => {
    setDateRange(dateRange);
    onChangeValue();
  };
  const onCancelItem = async () => {
    form.resetFields();
    setValuesUnchanged(true);
    setCustomError('');
    setSelectedDeviceType(licenseItemState.item.value.deviceType);
    setCanCreateUnlimitedLicense(licenseStateValue.unlimited.value);
    setDateRange(undefined);
  };

  const onChangeValue = async () => {
    if (
      form.getFieldValue('toolCode') === licenseStateValue.toolCode.value &&
      form.getFieldValue('deviceType') === licenseStateValue.deviceType.value &&
      form.getFieldValue('fromDate') === licenseStateValue.fromDate.value &&
      form.getFieldValue('untilDate') === licenseStateValue.untilDate.value &&
      form.getFieldValue('unlimited') === licenseStateValue.unlimited.value &&
      form.getFieldValue('notes') === licenseStateValue.notes.value &&
      form.getFieldValue('project') === licenseStateValue.notes.value &&
      form.getFieldValue('customer') === licenseStateValue.notes.value &&
      form.getFieldValue('workingHours') === licenseStateValue.workingHours.value &&
      form.getFieldValue('isRecurrent') === licenseStateValue.isRecurrent.value &&
      form.getFieldValue('coreDiameter') === licenseStateValue.toolCode.value[0]
    )
      setValuesUnchanged(true);
    else setValuesUnchanged(false);
  };

  const onProjectSearch = (searchText: string) => {
    setProjectOptions(
      !searchText || !licenseItemState.value.item.projects
        ? []
        : licenseItemState.value.item.projects.filter((x) => x.toLowerCase().includes(searchText.toLowerCase()))
    );
  };
  const onCustomerSearch = (searchText: string) => {
    setCustomerOptions(
      !searchText || !licenseItemState.value.item.customers
        ? []
        : licenseItemState.value.item.customers.filter((x) => x.toLowerCase().includes(searchText.toLowerCase()))
    );
  };
  const updateSignOff = () => {
    var currentValue = licenseStateValue.signedOffOn.value;
    licenseItemStore.updateSignOff({ signOff: currentValue === undefined }, licenseStateValue.id.value).then((response) => {
      if (response?.data?.isSuccess) {
        licenseItemStore.load(licenseDataId);
      }
    });
  };
  const onCanCreateUnlimitedLicenseChange = async (e: any) => {
    setCanCreateUnlimitedLicense(e.target.checked);
    setValuesUnchanged(false);
  };

  const getDeviceOptions = () => {
    const deviceList = licenseCreationAttributesState.item.value
      ? licenseCreationAttributesState.item.value.allowedDevices
      : Object.values(DeviceType);

    return deviceList.toSorted(deviceTypeSorter).map((y) => {
      return { label: t(deviceTypeLabels[y]), value: y };
    });
  };
  return (
    <Spin
      spinning={
        isBusy.get() || licenseDataState.isBusy.get() || licenseCreationAttributesState.isBusy.get() || canCreateUnlimitedLicense === undefined
      }
    >
      {blocker.state === 'blocked' ? (
        <Modal open onOk={() => blocker.proceed()} onCancel={() => blocker.reset()}>
          <span>{t('UnsavedValuesWarning')}</span>
        </Modal>
      ) : null}
      {licenseStateValue && licenseStateValue.value && licenseStateValue.id.value === licenseDataId && (
        <div>
          <Card style={{ padding: 40 }}>
            {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
            <Form form={form} layout="vertical" autoComplete="off">
              <Form form={form} layout="inline" autoComplete="off" style={{ marginBottom: 20 }}>
                <Row gutter={[10, 12]} style={{ width: '100%' }}>
                  <Col lg={6} xs={24}>
                    <Form.Item
                      name={nameof<LicenseDataItem>('deviceType')}
                      style={{ width: '100%' }}
                      initialValue={
                        !licenseCreationAttributesState.item.value ||
                        licenseCreationAttributesState.item.value.allowedDevices.includes(licenseStateValue.deviceType.value)
                          ? licenseStateValue.deviceType.value
                          : deviceTypeLabels[licenseStateValue.deviceType.value]
                      }
                      label={t('DeviceType')}
                    >
                      <Select
                        onSelect={(value: DeviceType) => {
                          setSelectedDeviceType(value);
                          if (value === DeviceType.SCTD && form.getFieldValue('toolCode')?.length === 5) {
                            form.setFieldsValue({ toolCode: '' });
                          }
                        }}
                        style={{ width: '100%' }}
                        placeholder={t('DeviceType')}
                        options={getDeviceOptions()}
                        onChange={() => onChangeValue()}
                      />
                    </Form.Item>
                  </Col>

                  {(form.getFieldValue('deviceType') === DeviceType.SCTD ||
                    (form.getFieldValue('deviceType') === undefined && licenseStateValue.deviceType.value === DeviceType.SCTD)) && (
                    <Col lg={4} xs={24}>
                      <Form.Item
                        initialValue={
                          licenseStateValue.deviceType.value === DeviceType.SCTD ? parseInt(licenseStateValue.toolCode.value[0]) : undefined
                        }
                        label={t('ToolDiameter')}
                        name={'coreDiameter'}
                        rules={[{ required: true, message: t('PleaseEnterCoreDiameter') }]}
                        style={{ marginRight: 15 }}
                      >
                        <Select
                          onChange={() => onChangeValue()}
                          style={{ height: '100%' }}
                          placeholder={t('ToolDiameter')}
                          options={Object.entries(CoreDiameter)
                            .filter((v) => isNaN(parseInt(v[0])))
                            .map(([key, value]) => ({ label: key, value }))}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  <Col lg={6} xs={24}>
                    <Form.Item
                      name={nameof<LicenseDataItem>('toolCode')}
                      rules={[
                        { required: true, message: t('PleaseEnterToolCode') },
                        () => ({
                          validator(_, value: string) {
                            if (parseInt(value) === 0) return Promise.reject(t('InvalidToolcode'));
                            else if (parseInt(value) > 65535) return Promise.reject(t('ToolcodeTooLarge'));
                            return Promise.resolve();
                          },
                        }),
                      ]}
                      initialValue={getLicenseToolCode(licenseStateValue.value as LicenseDataItem)}
                      label={t('ToolCode')}
                    >
                      <Input
                        maxLength={form.getFieldValue('deviceType') === DeviceType.SCTD ? 4 : 5}
                        style={{ width: '100%' }}
                        autoComplete="off"
                        allowClear
                        placeholder={t('ToolCode')}
                        onChange={(e) => {
                          const regex = /[^0-9]/g;
                          form.setFieldsValue({ toolCode: e.target.value.replace(regex, '') });
                          onChangeValue();
                        }}
                      />
                    </Form.Item>
                  </Col>
                  {(!licenseCreationAttributesState.item.value || licenseCreationAttributesState.item.value.canCreateUnlimitedLicense) && (
                    <Col xl={2}>
                      <Form.Item
                        name={nameof<LicenseDataItem>('unlimited')}
                        label={t('Unlimited')}
                        valuePropName="checked"
                        initialValue={licenseStateValue.unlimited.value}
                      >
                        <Checkbox onChange={onCanCreateUnlimitedLicenseChange} />
                      </Form.Item>
                    </Col>
                  )}
                  {!canCreateUnlimitedLicense && (
                    <>
                      {!isHourBasedLicense(selectedDeviceType) && (
                        <Col xl={6}>
                          <Form.Item
                            name={'date-range'}
                            label={t('PeriodOfValidity')}
                            rules={[
                              {
                                validator: () => {
                                  if (!licenseItemState.item.value.unlimited && !isHourBasedLicense(licenseItemState.item.value.deviceType)) {
                                    return Promise.resolve();
                                  } else {
                                    return dateRange ? Promise.resolve() : Promise.reject(t('PleaseEnterStartAndEndDate'));
                                  }
                                },
                              },
                            ]}
                            style={{ width: '100%' }}
                          >
                            <RangePicker
                              classNames={{
                                popup: {
                                  root: 'responsive-range-picker',
                                },
                              }}
                              style={{ height: 40, width: '100%' }}
                              allowClear={false}
                              onChange={(dateRange: any) => onDateRangeEdit(dateRange)}
                              format={getDateFormat()}
                              defaultValue={
                                !licenseItemState.item.value.unlimited && !isHourBasedLicense(licenseItemState.item.value.deviceType)
                                  ? [
                                      moment((licenseStateValue.value as LicenseDataItem).fromDate).utc(),
                                      moment((licenseStateValue.value as LicenseDataItem).untilDate).utc(),
                                    ]
                                  : undefined
                              }
                            />
                          </Form.Item>
                        </Col>
                      )}
                      {isHourBasedLicense(selectedDeviceType) && (
                        <Col xl={6}>
                          <Form.Item
                            name={nameof<LicenseDataItem>('workingHours')}
                            label={t('WorkingHours')}
                            rules={[{ required: !canCreateUnlimitedLicense, message: t('PleaseEnterWorkingHours') }]}
                            initialValue={
                              isHourBasedLicense(licenseItemState.item.value.deviceType) && !licenseItemState.item.value.unlimited
                                ? licenseItemState.item.value.workingHours
                                : undefined
                            }
                            style={{ width: '100%' }}
                          >
                            <Input
                              style={{ width: '100%' }}
                              autoComplete="off"
                              allowClear
                              placeholder={t('WorkingHours')}
                              onChange={(e) => {
                                const regex = /[^0-9]/g;
                                form.setFieldsValue({ workingHours: e.target.value.replace(regex, '') });
                                onChangeValue();
                              }}
                            />
                          </Form.Item>
                        </Col>
                      )}
                    </>
                  )}

                  <Col xl={12} sm={24} xs={24}>
                    <Form.Item name={nameof<LicenseDataItem>('project')} label={t('Project')} initialValue={licenseStateValue.project.value}>
                      <AutoComplete
                        options={projectOptions.map((x) => {
                          return { value: x };
                        })}
                        style={{ width: '100%', height: '100%' }}
                        onSearch={onProjectSearch}
                        placeholder={t('Project')}
                        onChange={() => onChangeValue()}
                      />
                    </Form.Item>
                  </Col>
                  <Col xl={12} sm={24} xs={24}>
                    <Form.Item name={nameof<LicenseDataItem>('customer')} label={t('Customer')} initialValue={licenseStateValue.customer.value}>
                      <AutoComplete
                        style={{ width: '100%', height: '100%' }}
                        options={customerOptions.map((x) => {
                          return { value: x };
                        })}
                        onSearch={onCustomerSearch}
                        placeholder={t('Customer')}
                        onChange={() => onChangeValue()}
                      />
                    </Form.Item>
                  </Col>
                  {licenseStateValue.signedOffByUserName.value && (
                    <>
                      <Col xl={12} sm={24}>
                        <Form.Item
                          name={nameof<LicenseDataItem>('signedOffByUserName')}
                          initialValue={licenseStateValue.signedOffByUserName.value}
                          label={t('SignedOffBy')}
                        >
                          <Input autoComplete="off" disabled />
                        </Form.Item>
                      </Col>
                      <Col xl={12} sm={24}>
                        <Form.Item
                          name={nameof<LicenseDataItem>('signedOffOn')}
                          initialValue={moment(licenseStateValue.signedOffOn.value as Date)
                            .locale(i18next.language)
                            .format(`${getDateFormat()}, hh:mm a`)}
                          label={t('SignedOffOn')}
                        >
                          <Input autoComplete="off" disabled />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                  <Col span={24}>
                    <Form.Item
                      style={{ width: 'calc(100%)' }}
                      name={nameof<LicenseDataItem>('notes')}
                      label={t('Notes')}
                      initialValue={licenseStateValue.notes.value}
                    >
                      <TextArea placeholder={t('Notes')} autoComplete="off" rows={3} allowClear onChange={() => onChangeValue()} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name={nameof<LicenseDataItem>('isRecurrent')}
                      valuePropName="checked"
                      initialValue={licenseStateValue.isRecurrent.value}
                    >
                      <Checkbox onChange={onChangeValue}>{t('Recurrence')}</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>

              <Button type="default" disabled={valuesUnchanged} onClick={() => onCancelItem()} style={{ margin: '10px' }}>
                {t('Cancel')}
              </Button>
              {licenseStateValue.value.needsSignOff && canApproveLicenses && (
                <Button type="primary" onClick={() => updateSignOff()} style={{ margin: '10px' }}>
                  {licenseStateValue.value.signedOffByUserName ? t('Refuse') : t('SignOff')}
                </Button>
              )}
              <Button type="primary" disabled={valuesUnchanged} onClick={() => validateLicense()} style={{ margin: '10px' }}>
                {t('SaveChanges')}
              </Button>
              {(!licenseStateValue.needsSignOff.value || licenseStateValue.signedOffByUserName.value) && (
                <Button type="default" onClick={() => setLicenseVisible(true)} style={{ margin: '10px' }}>
                  {t('ShowLicense')}
                </Button>
              )}
            </Form>
          </Card>
        </div>
      )}
      {saveLicenseDataWarning && (
        <Modal
          title={<h3>{`${t('Confirmation')}`}</h3>}
          open={saveLicenseDataWarning}
          onOk={onEditItem}
          onCancel={() => setSaveLicenseDataWarning(false)}
          okText={t('OK')}
          okButtonProps={{ style: { width: '45%', float: 'right' } }}
          cancelText={t('Cancel')}
          cancelButtonProps={{ type: 'default', style: { width: '45%' } }}
        >
          <p>
            {existingLicenseRef.current?.unlimited
              ? t(
                  `EditWarningUnlimited${organizationIdRef.current === null ? 'Global' : ''}${
                    existingLicenseRef.current.deviceType === DeviceType.SCTD ? 'SmartCore' : ''
                  }LicenseAlreadyExists`
                )
                  .replace('{0}', `${deviceTypeLabels[existingLicenseRef.current?.deviceType]}`)
                  .replace('{1}', `${getLicenseToolCode(existingLicenseRef.current!)}`)
                  .replace('{2}', `${existingLicenseRef.current?.createdBy}`)
                  .replace('{3}', `${moment(existingLicenseRef.current?.createdOn).local().format(getDateFormat())}`)
                  .replace('{4}', `${existingLicenseRef.current?.organizationName}`)
                  .replace('{5}', `${getSmartCoreLicenseToolDiameter(existingLicenseRef.current)}`)
              : t(
                  `EditWarning${organizationIdRef.current === null ? 'Global' : ''}${
                    existingLicenseRef.current!.deviceType === DeviceType.SCTD ? 'SmartCore' : ''
                  }LicenseAlreadyExists`
                )
                  .replace('{0}', `${deviceTypeLabels[existingLicenseRef.current!.deviceType]}`)
                  .replace('{1}', `${getLicenseToolCode(existingLicenseRef.current!)}`)
                  .replace('{2}', `${moment(existingLicenseRef.current?.fromDate).utc().format(getDateFormat())}`)
                  .replace('{3}', `${moment(existingLicenseRef.current?.untilDate).utc().format(getDateFormat())}`)
                  .replace('{4}', `${existingLicenseRef.current?.createdBy}`)
                  .replace('{5}', `${moment(existingLicenseRef.current?.createdOn).local().format(getDateFormat())}`)
                  .replace('{6}', `${existingLicenseRef.current?.organizationName}`)
                  .replace('{7}', `${getSmartCoreLicenseToolDiameter(existingLicenseRef.current!)}`)}
          </p>
        </Modal>
      )}
      {licenseVisible && (
        <ShowLicenseDialog
          licenseDataId={licenseDataId}
          onClose={() => {
            setLicenseVisible(false);
          }}
        />
      )}
    </Spin>
  );
};

export default LicenseInfoPage;
